import styled, { css } from "styled-components";
import Typography from "@pagepro-monorepo/ui/lib/components/styles/Typography";
import media from "@pagepro-monorepo/ui/lib/theme/config/media";

export const RISK_COLORS = {
  low: "#2e7d32",
  medium: "#ed6c02",
  high: "#d32f2f"
} as const;

export const Wrapper = styled.div`
  width: 100%;
`;

export const IntroCheckpoint = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.secondaryDecor};
  font-size: 0.875rem;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 1rem;
    height: 1rem;
    opacity: 0.6;
  }
`;

export const IntroOutcome = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};

  &::before {
    content: "";
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
`;

export const PillScroller = styled.div`
  display: none;
  gap: 0.5rem;
  margin-bottom: 1rem;

  @media ${media.tablet} {
    display: flex;
    flex-wrap: wrap;
  }
`;

export const BlockPillText = styled(Typography).attrs({
  forwardedAs: "span",
  variant: "body4"
})`
  color: ${({ theme }) => theme.colors.primary};
  transition: ${({ theme }) => theme.transition};
`;

export const BlockPillMark = styled.span`
  position: relative;
  border: 1px solid rgba(0, 20, 31, 0.2);
  height: 1.25rem;
  width: 1.25rem;
  border-radius: 100vh;
  display: flex;
  color: transparent;
  flex-shrink: 0;
  transition: ${({ theme }) => theme.transition};

  &::after {
    content: "";
    background-color: currentColor;
    height: 0.4rem;
    width: 0.4rem;
    border-radius: inherit;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export const BlockPill = styled.span<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  white-space: nowrap;
  border-radius: 100vh;

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
  cursor: default;
  transition: ${({ theme }) => theme.transition};
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 0px ${({ theme }) => theme.shadow.default};

  ${({ theme, $isActive }) =>
    $isActive
      ? css`
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
          box-shadow: 0px 0.625rem 0.9375rem ${theme.shadow.default};

          ${BlockPillText} {
            color: ${theme.colors.white};
          }

          ${BlockPillMark} {
            color: ${theme.colors.accent};
            border-color: rgba(255, 255, 255, 0.2);
          }
        `
      : css`
          border: 1px solid ${theme.colors.secondaryDecor};
        `}
`;

export const ProgressBar = styled.div`
  position: relative;
  height: 0.25rem;
  width: 100%;
  background-color: ${({ theme }) =>
    theme.colors.secondaryLightDecor};
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: width 0.5s ease-out;
  }
`;

export const OptionButton = styled.button<{ $isSelected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected
        ? theme.colors.primary
        : theme.colors.secondaryDecor};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.secondaryLightDecor
      : theme.colors.white};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.primary : theme.colors.secondary};
  font-size: 0.875rem;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-align: left;
  cursor: pointer;
  transition: ${({ theme }) => theme.transition};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) =>
      theme.colors.secondaryLightDecor};
  }

  &::before {
    content: "";
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    border: 2px solid
      ${({ theme, $isSelected }) =>
        $isSelected
          ? theme.colors.primary
          : theme.colors.secondaryDecor};
    flex-shrink: 0;
    transition: ${({ theme }) => theme.transition};
    ${({ theme, $isSelected }) =>
      $isSelected &&
      css`
        background: radial-gradient(
          ${theme.colors.primary} 40%,
          transparent 41%
        );
      `}
  }
`;

export const NavigationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid
    ${({ theme }) => theme.colors.secondaryLightDecor};
`;

export const ResultCard = styled.div<{
  $borderColor?: string;
  $bgColor?: string;
}>`
  border: 1px solid
    ${({ theme, $borderColor }) =>
      $borderColor || theme.colors.secondaryDecor};
  padding: 1.5rem;
  background-color: ${({ $bgColor }) => $bgColor || "transparent"};

  @media ${media.mobile} {
    padding: 2rem;
  }
`;

export const RiskBadge = styled.span<{
  $severity: "low" | "medium" | "high";
}>`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  font-size: 0.625rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ $severity }) => RISK_COLORS[$severity]};
  background-color: ${({ $severity }) =>
    `${RISK_COLORS[$severity]}15`};
  border: 1px solid
    ${({ $severity }) => `${RISK_COLORS[$severity]}30`};
`;

export const RiskRow = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.secondaryLightDecor};

  &:last-child {
    border-bottom: 0;
  }
`;

export const RiskLevelBanner = styled.div<{
  $level: "low" | "medium" | "high";
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border: 1px solid ${({ $level }) => `${RISK_COLORS[$level]}40`};
  background-color: ${({ $level }) => `${RISK_COLORS[$level]}10`};
  color: ${({ $level }) => RISK_COLORS[$level]};
`;

export const NarrowText = styled.div`
  margin-bottom: 1.5rem;

  @media ${media.tablet} {
    padding-right: 40%;
  }
`;

export const CtaCard = styled.div`
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};

  @media ${media.mobile} {
    padding: 2rem;
  }
`;

export const StepBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background-color: ${({ theme }) =>
    theme.colors.secondaryLightDecor};
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  flex-shrink: 0;
`;

export const SectionLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const RiskCategoryLabel = styled(Typography).attrs({
  variant: "body4",
  forwardedAs: "span"
})`
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const Footnote = styled.p`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const RestartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.secondaryDecor};
  transition: ${({ theme }) => theme.transition};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;
